import React from 'react';
import classnames from 'classnames';
import Label from "./Label.js";
import IdGen from "./IdGen.js";
import Theme from "./Theme.js";
import S from './RadioButton.css';
import Text from "./Text.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function EMPTY() {}

/** 
 * Creates a Radio button element in react 
 * @property {string} className a class to be assigned to the element 
 * @property {string} variant the variant name to be assigned to the component and label
 * @property {string} labelVariant the label variant name to be assigned to the label. Will be chained to variant
 * @property {string} labelSpacing the spacing between label and input. This will override the --size-radioLabelSpace variable
 * @property {string} label the optional label
 * @property {string} iconOn the icon displayed when the radio value === on 
 * @property {string} iconOff the icon displayed when radio value !== on 
 * @property {any} on value is compared to this to determine if the radio is selected 
 * @property {function} onChange callback called when element is clicked 
 * @property {any} eventData data fed to the onChange function 
 * @property {any} value a value used to determine if button is in an on state 
 * @property {string} id to be used by the element 
 * @property {boolean} disabled determines if the element is disabled or not 
 * @property {any} on @default [true] if equal to value, button is in and on state, defaults to true 
 * */
class RadioButton extends React.Component {
  static contextType = Theme.Context;

  // static propTypes = {
  //   className : PropTypes.string, // eslint-disable-line
  //   variant : PropTypes.string, // eslint-disable-line
  //   onChange : PropTypes.func,
  //   eventData : PropTypes.any,
  //   value : PropTypes.oneOfType( [ PropTypes.bool, PropTypes.string ] ),
  //   disabled : PropTypes.bool,
  //   stopPropagation : PropTypes.bool

  // }

  static defaultProps = {
    variant: null,
    className: null,
    onChange: null,
    eventData: null,
    value: false,
    disabled: false
  };
  constructor(props) {
    super(props);
    this.id = IdGen.createId('rb');
  }

  /** (Internal) the onChange of the element, calls the onChange prop */
  onClick = e => {
    const {
      onChange,
      eventData,
      disabled,
      value,
      on = true,
      stopPropagation
    } = this.props;
    if (stopPropagation !== false) {
      e.stopPropagation();
    }
    if (disabled) {
      return;
    }
    if (onChange) {
      this.input.focus();
      onChange({
        value: value === on,
        data: eventData,
        sourceEvent: e
      });
    }
  };
  render() {
    const theme = this.context;
    const {
      className,
      containerClassName,
      labelClassName,
      variant,
      iconOn = 'fa fa-circle',
      iconOff,
      onChange,
      tabIndex,
      label,
      labelVariant,
      labelSpace,
      eventData,
      value,
      id,
      disabled = false,
      on = true,
      layoutOptions,
      help,
      helpVariant,
      helpClassName,
      ...rest
    } = theme.props('RadioButton', this.props); // eslint-disable-line

    const hasLabel = label != null;
    const cn = theme.classes('RadioButton', 'radioButton', variant, className, S.radioButton);
    const labelClass = theme.subclasses('RadioButton', 'label', labelClassName, S.label);
    const containerClass = theme.subclasses('RadioButton', 'container', containerClassName, S.labelHolder);
    const helpClass = theme.subclasses('RadioButton', 'help', helpClassName, S.helpText);
    const ap = theme.subclasses('RadioButton', 'appearance', S.appearance);
    const mn = theme.subclasses('RadioButton', 'icon', S.icon);
    const disableValue = disabled === true ? 'disabled' : false;
    const calcId = id || (hasLabel ? this.id : null);
    const radio = /*#__PURE__*/_jsxs("div", {
      className: cn,
      onClick: this.onClick,
      "data-value": value === on,
      disabled: disabled === true ? 'disabled' : false,
      children: [/*#__PURE__*/_jsx("input", {
        ref: e => {
          this.input = e;
        },
        type: "radio",
        checked: value === on,
        id: calcId,
        tabIndex: tabIndex,
        onChange: EMPTY
      }), /*#__PURE__*/_jsx("div", {
        className: ap,
        children: /*#__PURE__*/_jsx("i", {
          className: classnames(value === on ? iconOn : iconOff, mn)
        })
      })]
    });
    if (label != null) {
      const style = {
        '--count-gridNumRows': help ? 2 : 1
      };
      if (labelSpace != null) {
        style['--size-radioLabelSpace'] = labelSpace[0] === '-' ? `var(${labelSpace})` : labelSpace;
      }
      let lv = [];
      if (labelVariant) {
        lv.push(labelVariant);
      }
      if (variant) {
        lv.push(variant);
      }
      lv = lv.length === 0 ? null : lv.join('|');
      const labelComp = /*#__PURE__*/_jsx(Label, {
        variant: lv,
        htmlFor: calcId,
        className: labelClass,
        disabled: disableValue,
        children: label
      });
      const helpComp = typeof help === 'string' ? /*#__PURE__*/_jsx(Text, {
        variant: helpVariant,
        className: helpClass,
        children: help
      }) : help;
      return /*#__PURE__*/_jsxs("div", {
        className: containerClass,
        style: style,
        children: [radio, " ", labelComp, /*#__PURE__*/_jsx("div", {}), " ", helpComp]
      });
    }
    return radio;
  }
}
export default RadioButton;